import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/useAuthStore'
import { supabase } from '../lib/supabase'

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--tco-surface)',
  border: '1px solid var(--tco-line)',
  borderRadius: 'var(--tco-radius)',
  padding: '0.625rem 0.75rem',
  color: 'var(--tco-ink)',
  fontSize: '15px',
  fontFamily: 'var(--tco-font-sans)',
  fontWeight: 400,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s, box-shadow 0.15s',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '11px',
  fontFamily: 'var(--tco-font-sans)',
  fontWeight: 600,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--tco-ink-3)',
  marginBottom: '0.5rem',
}

const errorStyle: React.CSSProperties = {
  marginBottom: '1rem',
  padding: '0.625rem 0.75rem',
  background: 'var(--tco-neg-soft)',
  border: '1px solid var(--tco-neg)',
  borderRadius: 'var(--tco-radius)',
  color: 'var(--tco-neg)',
  fontSize: '13px',
  fontFamily: 'var(--tco-font-sans)',
}

const submitBtnStyle = (disabled: boolean): React.CSSProperties => ({
  width: '100%',
  background: disabled ? 'var(--tco-line-soft)' : 'var(--tco-ink)',
  border: `1px solid ${disabled ? 'var(--tco-line)' : 'var(--tco-ink)'}`,
  color: disabled ? 'var(--tco-ink-3)' : 'var(--tco-bg)',
  borderRadius: 'var(--tco-radius)',
  padding: '0.625rem',
  fontSize: '14px',
  fontFamily: 'var(--tco-font-sans)',
  fontWeight: 550,
  letterSpacing: '0.04em',
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'background 0.15s',
})

function EmailSentState({ heading, sub }: { heading: string; sub: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'var(--tco-accent-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1rem',
        }}
      >
        <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="var(--tco-accent)" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      <p style={{ color: 'var(--tco-ink)', fontFamily: 'var(--tco-font-sans)', fontWeight: 600, marginBottom: '0.5rem' }}>{heading}</p>
      <p style={{ color: 'var(--tco-ink-2)', fontSize: '14px', fontFamily: 'var(--tco-font-sans)', fontWeight: 400, lineHeight: 1.5 }}>{sub}</p>
    </div>
  )
}

export default function LoginPage() {
  const { signInWithMagicLink, signInWithPassword, signUpWithPassword, loading } = useAuthStore()
  const navigate = useNavigate()

  // Phase 25: opt the body into the ledger theme on /login as well, so font
  // stack + tabular numerals + paper background are consistent with the rest
  // of the app. Layout.tsx applies the same class on protected routes.
  useEffect(() => {
    document.body.classList.add('tco-ledger')
    return () => {
      document.body.classList.remove('tco-ledger')
    }
  }, [])

  const [activeTab, setActiveTab] = useState<'magic' | 'password'>('magic')

  // Magic link state
  const [magicEmail, setMagicEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [sentEmail, setSentEmail] = useState('')
  const [magicError, setMagicError] = useState<string | null>(null)

  // Password state
  const [pwEmail, setPwEmail] = useState('')
  const [password, setPassword] = useState('')
  const [pwMode, setPwMode] = useState<'signin' | 'signup'>('signin')
  const [pwError, setPwError] = useState<string | null>(null)
  const [signupSent, setSignupSent] = useState(false)
  const [forgotSuccess, setForgotSuccess] = useState(false)

  async function handleMagicSubmit(e: React.FormEvent) {
    e.preventDefault()
    setMagicError(null)
    const { error } = await signInWithMagicLink(magicEmail.trim())
    if (error) {
      setMagicError(error)
    } else {
      setSentEmail(magicEmail.trim())
      setSent(true)
    }
  }

  async function handleForgotPassword() {
    setPwError(null)
    setForgotSuccess(false)
    if (!pwEmail.trim()) {
      setPwError('Please enter your email address above first')
      return
    }
    const { error } = await supabase.auth.resetPasswordForEmail(pwEmail.trim(), {
      redirectTo: window.location.origin + '/auth/callback',
    })
    if (error) {
      console.log('resetPasswordForEmail error:', error)
      setPwError(error.message)
    } else {
      setForgotSuccess(true)
    }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault()
    setPwError(null)
    if (pwMode === 'signin') {
      const { error } = await signInWithPassword(pwEmail.trim(), password)
      if (error) {
        setPwError(error)
      } else {
        navigate('/')
      }
    } else {
      const { error } = await signUpWithPassword(pwEmail.trim(), password)
      if (error) {
        setPwError(error)
      } else {
        setSignupSent(true)
      }
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--tco-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        fontFamily: 'var(--tco-font-sans)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          background: 'var(--tco-surface)',
          border: '1px solid var(--tco-line)',
          borderLeft: '3px solid var(--tco-ink)',
          borderRadius: 'var(--tco-radius)',
          padding: '2rem',
          position: 'relative',
        }}
      >
        {/* Logo / title */}
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <div
            style={{
              fontSize: '1.6rem',
              fontFamily: 'var(--tco-font-sans)',
              fontWeight: 700,
              color: 'var(--tco-ink)',
              letterSpacing: '-0.01em',
              marginBottom: '0.25rem',
            }}
          >
            VehicleTCO
          </div>
          <div style={{ fontSize: '14px', color: 'var(--tco-ink-2)', fontFamily: 'var(--tco-font-sans)', fontWeight: 400 }}>
            Sign in or create your account
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--tco-line)', marginBottom: '1.5rem' }}>
          {(['magic', 'password'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: '0.5rem',
                fontSize: '13px',
                fontFamily: 'var(--tco-font-sans)',
                fontWeight: activeTab === tab ? 600 : 400,
                letterSpacing: '0.04em',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: activeTab === tab ? 'var(--tco-ink)' : 'var(--tco-ink-3)',
                borderBottom: activeTab === tab ? '2px solid var(--tco-neg)' : '2px solid transparent',
                marginBottom: '-1px',
                transition: 'color 0.15s',
              }}
            >
              {tab === 'magic' ? 'Magic Link' : 'Password'}
            </button>
          ))}
        </div>

        {/* ── Magic Link tab ─────────────────────────────────────────────── */}
        {activeTab === 'magic' && (
          sent ? (
            <>
              <EmailSentState
                heading="Check your email"
                sub={`We sent a login link to ${sentEmail}. You can close this tab after clicking the link.`}
              />
              {/* Rate-limit guidance belongs AFTER a send, not on first view. */}
              <p style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--tco-ink-3)', fontSize: '12px', lineHeight: 1.5 }}>
                Didn't receive the email? Wait 30 minutes before requesting another link, or use the Password tab to sign in instantly.
              </p>
            </>
          ) : (
            <form onSubmit={handleMagicSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="magic-email" style={labelStyle}>Email address</label>
                <input
                  id="magic-email"
                  type="email"
                  required
                  value={magicEmail}
                  onChange={(e) => setMagicEmail(e.target.value)}
                  placeholder="you@example.com"
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--tco-ink)'; e.target.style.boxShadow = '0 0 0 2px rgba(23,24,26,0.08)' }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--tco-line)'; e.target.style.boxShadow = 'none' }}
                />
              </div>

              {magicError && <div style={errorStyle}>{magicError}</div>}

              <button
                type="submit"
                disabled={loading || !magicEmail.trim()}
                style={submitBtnStyle(loading || !magicEmail.trim())}
              >
                {loading ? 'Sending…' : 'Send Login Link'}
              </button>

              <p style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--tco-ink-3)', fontSize: '13px' }}>
                New users are automatically registered on first login.
              </p>
            </form>
          )
        )}

        {/* ── Password tab ───────────────────────────────────────────────── */}
        {activeTab === 'password' && (
          signupSent ? (
            <EmailSentState
              heading="Check your email to confirm your account"
              sub="After confirming, return here to sign in."
            />
          ) : (
            <form onSubmit={handlePasswordSubmit}>
              {/* Sign In / Create Account toggle */}
              <div
                style={{
                  display: 'flex',
                  background: 'var(--tco-line-soft)',
                  border: '1px solid var(--tco-line)',
                  borderRadius: 'var(--tco-radius)',
                  padding: '3px',
                  marginBottom: '1.25rem',
                }}
              >
                {(['signin', 'signup'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => { setPwMode(mode); setPwError(null); setForgotSuccess(false) }}
                    style={{
                      flex: 1,
                      padding: '0.4rem',
                      fontSize: '13px',
                      fontWeight: 500,
                      border: 'none',
                      borderRadius: 'var(--tco-radius-sm)',
                      cursor: 'pointer',
                      background: pwMode === mode ? 'var(--tco-surface)' : 'transparent',
                      color: pwMode === mode ? 'var(--tco-ink)' : 'var(--tco-ink-2)',
                      fontFamily: 'var(--tco-font-sans)',
                      letterSpacing: '0.02em',
                      boxShadow: pwMode === mode ? '0 1px 2px rgba(23,24,26,0.06)' : 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    {mode === 'signin' ? 'Sign In' : 'Create Account'}
                  </button>
                ))}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="pw-email" style={labelStyle}>Email address</label>
                <input
                  id="pw-email"
                  type="email"
                  required
                  value={pwEmail}
                  onChange={(e) => setPwEmail(e.target.value)}
                  placeholder="you@example.com"
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--tco-ink)'; e.target.style.boxShadow = '0 0 0 2px rgba(23,24,26,0.08)' }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--tco-line)'; e.target.style.boxShadow = 'none' }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="pw-password" style={labelStyle}>Password</label>
                <input
                  id="pw-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={pwMode === 'signup' ? 'Minimum 6 characters' : '••••••••'}
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--tco-ink)'; e.target.style.boxShadow = '0 0 0 2px rgba(23,24,26,0.08)' }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--tco-line)'; e.target.style.boxShadow = 'none' }}
                />
              </div>

              {pwError && <div style={errorStyle}>{pwError}</div>}

              <button
                type="submit"
                disabled={loading || !pwEmail.trim() || !password}
                style={submitBtnStyle(loading || !pwEmail.trim() || !password)}
              >
                {loading ? 'Working…' : pwMode === 'signin' ? 'Sign In' : 'Create Account'}
              </button>

              {pwMode === 'signin' && (
                <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                  {forgotSuccess ? (
                    <p style={{ color: 'var(--tco-accent)', fontSize: '13px', margin: 0 }}>
                      Password reset email sent.
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--tco-ink-2)',
                        fontFamily: 'var(--tco-font-sans)',
                        fontSize: '13px',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        padding: 0,
                      }}
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
              )}
            </form>
          )
        )}
      </div>
    </div>
  )
}
